import { useCallback, useEffect, useState } from 'react';
import { Resource } from '@hooks/useCurrentUserInfo/types';
import { StoredSearchResult } from '@hooks/useSearchedAlbums/types';
import { DEFAULT_LIMIT, DEFAULT_PAGE } from '@helpers/constants';
import { ReleaseViewHistoryResult } from './types';

export const useReleaseViewHistory = (
  userFeeds?: Resource[],
  userLicensors?: Resource[],
): ReleaseViewHistoryResult => {
  const initial = window.localStorage.getItem('releaseViewHistory');
  const [releaseViewHistory, setReleaseViewHistory] = useState<
    StoredSearchResult[]
  >(initial ? JSON.parse(initial) : []);
  const [page, setPage] = useState(DEFAULT_PAGE);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const offset = (Number(page) - 1) * Number(limit);

  // Updates history based on user's current org
  useEffect(() => {
    if (userFeeds || userLicensors) {
      const filtered = releaseViewHistory.filter(release => {
        const userHasFeedAccess = userFeeds
          ?.map((feed: Resource) => feed.name)
          .includes(release.feedName);
        const userHasLicensorAccess = userLicensors
          ?.map((licensor: Resource) => licensor.name)
          .includes(release.licensorName);

        return userHasFeedAccess || userHasLicensorAccess;
      });
      if (filtered.length !== releaseViewHistory.length) {
        setReleaseViewHistory(filtered);
      }
    }
  }, [userFeeds, userLicensors, releaseViewHistory]);

  // Updates history in local storage
  useEffect(() => {
    window.localStorage.setItem(
      'releaseViewHistory',
      JSON.stringify(releaseViewHistory),
    );
  }, [releaseViewHistory]);

  const tableRowMax = 50;
  const addToReleaseHistory = useCallback(
    (release: StoredSearchResult) => {
      let newArr = [];
      const historyIndex = releaseViewHistory.findIndex(
        item => item.uri === release.uri,
      );
      if (historyIndex > -1) {
        const removedExistingEntry = [...releaseViewHistory];
        removedExistingEntry.splice(historyIndex, 1);
        newArr = [release, ...removedExistingEntry];
      } else {
        newArr = [release, ...releaseViewHistory];
      }

      setReleaseViewHistory(
        newArr.length > tableRowMax ? newArr.slice(0, tableRowMax) : newArr,
      );
    },
    [releaseViewHistory],
  );

  const onPagination = useCallback((updatedPage: number) => {
    setPage(updatedPage.toString());
  }, []);

  const onLimitChange = useCallback((updatedLimit: number) => {
    setLimit(updatedLimit.toString());
  }, []);

  return {
    data: {
      releaseViewHistory: releaseViewHistory.slice(
        offset,
        offset + Number(limit) > releaseViewHistory.length
          ? undefined
          : offset + Number(limit),
      ),
      page,
      limit,
    },
    actions: {
      addToReleaseHistory,
      onPagination,
      onLimitChange,
    },
  };
};
